import { useEffect, useState, type ChangeEvent } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store';
import { tenantService, type TenantResponse } from '@/services/tenantService';
import { readActingTenant } from '@/utils/actingTenant';
import { isSelectableSubscription } from '@/utils/tenantSelectable';
import { switchActingTenant } from '@/utils/switchActingTenant';

function ownTenantLabel(chosenCode: string, ownCode: string, tenants: TenantResponse[]): string {
  if (chosenCode) return 'Own tenant';
  if (!ownCode) return 'No tenant';
  const ownName = tenants.find(tenant => tenant.tenant_code === ownCode)?.name;
  return `Own tenant (${ownName ?? ownCode})`;
}

const ActingTenantSelector = () => {
  const ownCode = useSelector((state: RootState) => state.auth.user?.tenant_code) ?? '';
  const chosenCode = readActingTenant();
  const [tenants, setTenants] = useState<TenantResponse[]>([]);

  useEffect(() => {
    let cancelled = false;
    tenantService
      .getTenants()
      .then(data => {
        if (!cancelled)
          setTenants(data.filter(tenant => isSelectableSubscription(tenant.subscription_status)));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    switchActingTenant(event.target.value);
    window.location.reload();
  };

  const chosenIsListed = tenants.some(tenant => tenant.tenant_code === chosenCode);

  return (
    <select
      aria-label="Acting tenant"
      value={chosenCode}
      onChange={handleChange}
      className="max-w-[12rem] rounded-md border border-gray-200 bg-white py-1 pl-2 pr-8 text-sm text-gray-700 focus:border-blue-500 focus:ring-blue-500"
    >
      <option value="">{ownTenantLabel(chosenCode, ownCode, tenants)}</option>
      {chosenCode && !chosenIsListed && <option value={chosenCode}>{chosenCode}</option>}
      {tenants.map(tenant => (
        <option key={tenant.id} value={tenant.tenant_code}>
          {tenant.name}
        </option>
      ))}
    </select>
  );
};

export default ActingTenantSelector;
